import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { choicesFor, fits, RELATION_NAMES, orbit, type Generation, type Relation } from '../../../shared/tree'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api } from '@/lib/api'
import { type PersonFields, type Tree, type TreePerson } from '@/lib/tree'
import { Avatar } from './Avatar'
import { PersonForm } from './PersonForm'

// "Add a family connection", from someone's card: what are they to this person, then who. Two taps for someone
// already in the tree; a name for someone new. Adding someone to the tree invites them nowhere, and says so. A pet
// only has its people, so from a pet's card it goes straight to who. A grandparent or grandchild is added through the
// parent or child in between (see Through). heading and onBack are for that: a step inside a bigger question.
export function AddConnection({
  familyId,
  of,
  tree,
  start = null,
  heading,
  onBack,
  onDone,
}: {
  familyId: string
  of: TreePerson
  tree: Tree
  start?: Relation | Generation | null
  heading?: string
  onBack?: () => void
  onDone: (personId: string) => void
}) {
  const queryClient = useQueryClient()
  const choices = heading ? [] : choicesFor(of)
  const [relation, setRelation] = useState<Relation | Generation | null>(
    start ?? (choices.length === 1 ? choices[0]! : null),
  )
  const [query, setQuery] = useState('')
  const [someoneNew, setSomeoneNew] = useState(false)
  const first = of.name

  const finish = async (personId: string) => {
    await queryClient.invalidateQueries({ queryKey: ['tree', familyId] })
    await queryClient.invalidateQueries({ queryKey: ['person'] })
    onDone(personId)
  }
  const link = useMutation({
    mutationFn: (personId: string) =>
      api(`/families/${familyId}/connections`, {
        method: 'POST',
        body: { of: of.id, as: relation, person: personId },
      }).then(() => personId),
    onSuccess: finish,
  })
  const create = useMutation({
    mutationFn: (fields: PersonFields) =>
      api<{ id: string }>(`/families/${familyId}/people`, {
        method: 'POST',
        body: { ...fields, connect: { of: of.id, as: relation } },
      }).then((x) => x.id),
    onSuccess: finish,
  })

  if (relation === 'grandparent' || relation === 'grandchild') {
    return (
      <Through
        familyId={familyId}
        of={of}
        tree={tree}
        relation={relation}
        onBack={() => setRelation(null)}
        onDone={onDone}
      />
    )
  }

  if (!relation) {
    return (
      <fieldset className="grid gap-3">
        <legend className="mb-3 text-xl font-semibold tracking-tight">What are they to {first}?</legend>
        <div className="flex flex-wrap gap-2">
          {choices.map((r) => (
            <Button key={r} variant="outline" onClick={() => setRelation(r)}>
              {RELATION_NAMES[r]}
            </Button>
          ))}
        </div>
      </fieldset>
    )
  }

  // Everyone who could be this, leaving out anyone already tied to them directly, and pets unless it's a pet.
  const near = new Set(
    Object.values(orbit(of.id, tree.links))
      .flat()
      .filter((n) => n.link)
      .map((n) => n.id),
  )
  const q = query.trim().toLowerCase()
  const candidates = tree.people.filter(
    (p) => p.id !== of.id && !near.has(p.id) && fits(of, relation, p) && (!q || p.name.toLowerCase().includes(q)),
  )
  const title = heading ?? `${RELATION_NAMES[relation]} of ${first}`
  const back = heading ? onBack : choices.length > 1 ? () => setRelation(null) : undefined

  return (
    <section className="grid gap-5" aria-label={title}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
        {back && (
          <Button variant="link" size="sm" onClick={back}>
            Change
          </Button>
        )}
      </div>

      {!someoneNew && (
        <>
          {tree.people.length > 8 && (
            <Input
              type="search"
              aria-label="Find someone in the tree"
              placeholder="Find someone in the tree"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          {candidates.length > 0 && (
            <ul className="grid gap-1" aria-label="Someone already in the tree">
              {candidates.slice(0, 30).map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    disabled={link.isPending}
                    onClick={() => link.mutate(p.id)}
                    className="flex min-h-12 w-full items-center gap-3 rounded-2xl px-2 text-left outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    <Avatar person={p} className="size-9 text-sm" />
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <FormError error={link.error} />
          <Button
            variant={candidates.length ? 'outline' : 'default'}
            className="self-start"
            onClick={() => setSomeoneNew(true)}
          >
            Someone new
          </Button>
        </>
      )}

      {someoneNew && (
        <PersonForm
          label="Add to the tree"
          pet={relation === 'pet'}
          pending={create.isPending}
          error={create.error}
          onSave={(fields) => create.mutate(fields)}
          aside={({ name, referAs }) => {
            if (relation === 'pet') return null
            const them = !name.trim() ? 'them' : referAs === 'she' ? 'her' : referAs === 'he' ? 'him' : 'them'
            return (
              <p className="text-sub text-muted-foreground">
                Adding {name.trim() || 'someone'} to the tree doesn’t give {them} access to any spaces. You can invite{' '}
                {them} separately.
              </p>
            )
          }}
        />
      )}
    </section>
  )
}

// A grandparent is a parent's parent, so it's added in two steps: which of their parents it's through (or that parent,
// first, when they aren't in the tree yet), then the grandparent as that parent's parent. The same downward for a
// grandchild. Only the two parent links are stored; the tree works out the rest.
function Through({
  familyId,
  of,
  tree,
  relation,
  onBack,
  onDone,
}: {
  familyId: string
  of: TreePerson
  tree: Tree
  relation: Generation
  onBack: () => void
  onDone: (personId: string) => void
}) {
  const up = relation === 'grandparent'
  const step = up ? 'parent' : 'child'
  const people = new Map(tree.people.map((p) => [p.id, p]))
  const around = orbit(of.id, tree.links)
  const options = (up ? around.parents : around.children).flatMap((n) => people.get(n.id) ?? [])
  const [viaId, setVia] = useState<string | null>(null)
  const via = viaId ? people.get(viaId) : undefined
  const [addingVia, setAddingVia] = useState(options.length === 0)
  const title = `${RELATION_NAMES[relation]} of ${of.name}`

  if (via) {
    return (
      <AddConnection
        familyId={familyId}
        of={via}
        tree={tree}
        start={step}
        heading={`${title}, through ${via.name}`}
        onBack={() => setVia(null)}
        onDone={onDone}
      />
    )
  }
  if (addingVia) {
    // The parent or child in between comes first; once they're in, it's on to the grandparent or grandchild.
    return (
      <div className="grid gap-4">
        <p className="text-sub text-muted-foreground">
          First, {of.name}’s {step} on that side. Then the {RELATION_NAMES[relation].toLowerCase()}.
        </p>
        <AddConnection
          familyId={familyId}
          of={of}
          tree={tree}
          start={step}
          heading={`${RELATION_NAMES[step]} of ${of.name}`}
          onBack={options.length ? () => setAddingVia(false) : onBack}
          onDone={(id) => {
            setAddingVia(false)
            setVia(id)
          }}
        />
      </div>
    )
  }
  return (
    <section className="grid gap-5" aria-label={title}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
        <Button variant="link" size="sm" onClick={onBack}>
          Change
        </Button>
      </div>
      <fieldset className="grid gap-2">
        <legend className="mb-2 text-sub text-muted-foreground">Through which {step}?</legend>
        <ul className="grid gap-1" aria-label={`${of.name}’s ${up ? 'parents' : 'children'}`}>
          {options.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setVia(p.id)}
                className="flex min-h-12 w-full items-center gap-3 rounded-2xl px-2 text-left outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <Avatar person={p} className="size-9 text-sm" />
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </fieldset>
      <Button variant="outline" className="self-start" onClick={() => setAddingVia(true)}>
        Another {step}
      </Button>
    </section>
  )
}
